import React, { useEffect, useRef, useState } from "react";
import { DraftPost } from "../types";
import { ConnectedProfile } from "../../../../types/customer";
import { asConnectedProfileArray } from "../utils/connectedProfiles";
import { getConnectedLinkedInTargets } from "../../../../utils/allowedPlatformMapping";
import { isGoogleBusinessProfilePlatform } from "./GoogleGMark";
import ScheduleDateTimeField from "../../../Shared/ScheduleDateTimeField";
import { formatInTimezone, resolveScheduleTimezone } from "../../../../utils/scheduleTime";
import { useActiveScheduleTimezone } from "../../../../hooks/useScheduleTimezone";
import {
  InstagramCanvasPreview,
  getInstagramFormat,
} from "./InstagramCanvasPreview";
import { useInstagramCanvasDraft } from "../hooks/useInstagramCanvasDraft";
import { instagramDraftHasPublishableMedia } from "../../../../utils/instagramPublishMediaValidation";
import LivePostLink from "../../../PostManager/LivePostLink";

type PlatformVariant = "linkedin" | "instagram" | "facebook" | "youtube" | "google_business" | "default";

function getPlatformVariant(platform: string): PlatformVariant {
  const p = platform.toLowerCase();
  if (p.includes("linkedin")) return "linkedin";
  if (p.includes("instagram")) return "instagram";
  if (p.includes("facebook")) return "facebook";
  if (p.includes("youtube")) return "youtube";
  if (isGoogleBusinessProfilePlatform(platform)) return "google_business";
  return "default";
}

// Rendered on the post's own timezone with its label. Rows without a stored
// zone fall back to the viewer's, matching their previous display.
function formatScheduledTime(value?: string, tz?: string): string {
  if (!value) return "";
  return formatInTimezone(value, tz);
}

// Low-fidelity internal SVGs for accurate logos
export const PlatformLogos = {
  facebook: (className?: string) => (
    <svg viewBox="0 0 24 24" className={className || "w-6 h-6"} fill="currentColor">
      <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.13v-3.622h3.13v-2.673c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
    </svg>
  ),
  instagram: (className?: string) => (
    <svg viewBox="0 0 24 24" className={className || "w-6 h-6"} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  ),
  linkedin: (className?: string) => (
    <svg viewBox="0 0 24 24" className={className || "w-6 h-6"} fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.454C23.205 24 24 23.227 24 22.271V1.729C24 .774 23.205 0 22.225 0z" />
    </svg>
  ),
  youtube: (className?: string) => (
    <svg viewBox="0 0 24 24" className={className || "w-6 h-6"} fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  ),
};

export type PostCardUploadMedia = {
  onLibrary: () => void;
  onLocalFile: () => void;
  /** Instagram carousel: multi-select from My Library */
  onLibraryCarousel?: () => void;
  /** LinkedIn document posts (SGL-174): pick a PDF from My Library. */
  onLibraryDocument?: () => void;
};

export type PlatformCardActionCallbacks = {
  onSave: () => void;
  onSchedule: () => void;
  onPublish: () => void;
  /** No date/time chosen for this post, or platform not connected. */
  scheduleDisabled: boolean;
  /** Platform not connected in Connections (Publish). */
  publishDisabled: boolean;
  /** Whether this post’s platform has a connected account (for tooltips). */
  isPlatformConnected: boolean;
  /** This card is running an action (all buttons show loading/disabled). */
  busyAction: "save" | "publish" | "schedule" | null;
};

interface PlatformPostCardProps {
  post: DraftPost;
  uploadMedia?: PostCardUploadMedia;
  onUpdate?: (updatedPost: DraftPost) => void;
  onEditingChange?: (isEditing: boolean) => void;
  showUnsavedError?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  connectedProfiles?: ConnectedProfile[];
  /** Per-card Save / Schedule / Publish (e.g. social posts right panel). */
  cardActions?: PlatformCardActionCallbacks;
}

const ConnectionStatusBadge: React.FC<{ isConnected: boolean }> = ({ isConnected }) => {
  if (isConnected) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-theme-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-theme-primary ring-1 ring-inset ring-theme-primary/20 uppercase tracking-widest leading-none">
        <span className="material-icons text-[10px] leading-none">check_circle</span>
        CONNECTED
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-theme-background px-1.5 py-0.5 text-[9px] font-bold text-theme-fg-muted ring-1 ring-inset ring-theme-border uppercase tracking-widest leading-none">
      <span className="material-icons text-[10px] leading-none">warning</span>
      NOT CONNECTED
    </span>
  );
};

const CONNECT_TOOLTIP = "Please connect this platform to enable this button.";
const INSTAGRAM_IMAGE_REQUIRED_MESSAGE =
  "Add an image or video before scheduling or publishing on Instagram (Reels need a video).";

const CardActionButtons: React.FC<{
  actions: PlatformCardActionCallbacks;
  validationMessage?: string | null;
}> = ({ actions, validationMessage }) => {
  const busy = actions.busyAction !== null;
  const spin = (kind: "save" | "schedule" | "publish") =>
    actions.busyAction === kind ? (
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
    ) : null;

  const scheduleTooltip = (() => {
    if (!actions.scheduleDisabled || busy) return undefined;
    if (!actions.isPlatformConnected) return CONNECT_TOOLTIP;
    return "Pick a date and time in the editor before scheduling.";
  })();

  const publishTooltip =
    actions.publishDisabled && !busy ? CONNECT_TOOLTIP : undefined;

  return (
    <div className="border-b border-theme-border bg-theme-background/60 px-4 py-3">
      <div className="grid grid-cols-1 gap-2 min-[400px]:grid-cols-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            actions.onSave();
          }}
          disabled={busy}
          className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl border border-theme-border bg-theme-surface px-3 text-sm font-semibold text-theme-fg transition-colors hover:bg-theme-background disabled:cursor-not-allowed disabled:opacity-50"
        >
          {spin("save")}
          {actions.busyAction === "save" ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            actions.onSchedule();
          }}
          disabled={busy || actions.scheduleDisabled}
          title={scheduleTooltip}
          className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl border border-theme-border bg-theme-surface px-3 text-sm font-semibold text-theme-fg transition-colors hover:bg-theme-background disabled:cursor-not-allowed disabled:opacity-50"
        >
          {spin("schedule")}
          {actions.busyAction === "schedule" ? "Scheduling…" : "Schedule"}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            actions.onPublish();
          }}
          disabled={busy || actions.publishDisabled}
          title={publishTooltip}
          className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl bg-theme-primary px-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {spin("publish")}
          {actions.busyAction === "publish" ? "Publishing…" : "Publish"}
        </button>
      </div>
      {validationMessage ? (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
          {validationMessage}
        </div>
      ) : null}
    </div>
  );
};

const PostCardHeader: React.FC<{
  platform: string;
  scheduledTime?: string;
  scheduledTimezone?: string;
  isConnected: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  onEdit: () => void;
  variant: PlatformVariant;
}> = ({ platform, scheduledTime, scheduledTimezone, isConnected, isSelected, onToggleSelect, onEdit, variant }) => {
  const formattedTime = formatScheduledTime(scheduledTime, scheduledTimezone);
  
  const statusMessage = isConnected 
    ? (formattedTime ? `Scheduled for ${formattedTime}` : "Scheduled for immediate publish")
    : "Not connected. Save as draft.";
  const statusIcon = isConnected ? "public" : "save";

  // Normalize platform name to Title Case for cleaner look
  const displayPlatform = platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();

  return (
    <div className="flex items-center justify-between p-4 border-b border-theme-border/50">
      <div className="flex items-center gap-3">
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="h-4 w-4 cursor-pointer rounded border-theme-border text-theme-primary focus:ring-theme-primary/50"
          />
        )}
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-theme-surface shadow-sm ring-1 ring-black/5 overflow-hidden">
          {variant === "facebook" && PlatformLogos.facebook("w-6 h-6 text-theme-fg-muted")}
          {variant === "instagram" && PlatformLogos.instagram("w-6 h-6 text-theme-fg-muted")}
          {variant === "linkedin" && PlatformLogos.linkedin("w-6 h-6 text-theme-fg-muted")}
          {variant === "youtube" && PlatformLogos.youtube("w-6 h-6 text-theme-fg-muted")}
          {variant === "google_business" && <span className="material-icons text-2xl text-theme-fg-muted">campaign</span>}
          {variant === "default" && <span className="material-icons text-2xl text-theme-fg-muted">campaign</span>}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-bold text-theme-fg text-[14px] leading-none">{displayPlatform}</span>
            <ConnectionStatusBadge isConnected={isConnected} />
          </div>
          <div className="flex items-center gap-1 mt-1 text-[11px] text-theme-fg-muted font-medium">
            <span>{statusMessage}</span>
            <span className="material-icons text-[13px] opacity-60 leading-none">{statusIcon}</span>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="p-2 text-theme-fg-muted hover:text-theme-primary transition-colors duration-200"
      >
        <span className="material-icons text-[18px]">edit</span>
      </button>
    </div>
  );
};

const EmptyStatePlaceholder: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div 
    onClick={onClick}
    className="aspect-[1.91/1] w-full flex flex-col items-center justify-center gap-3 bg-theme-background/30 border-y border-theme-border/30 cursor-pointer group hover:bg-theme-background/50 transition-colors"
  >
    <div className="w-12 h-12 rounded-full bg-theme-fg-muted/10 flex items-center justify-center text-theme-fg-muted group-hover:scale-110 transition-transform">
      <span className="material-icons text-3xl">cloud_upload</span>
    </div>
    <span className="text-[13px] font-bold text-theme-fg-muted group-hover:text-theme-primary transition-colors">
      Upload image or video
    </span>
  </div>
);

type UploadMenuAnchor = "toolbar" | "media" | "edit" | "carouselAdd";

export const PlatformPostCard: React.FC<PlatformPostCardProps> = ({
  post,
  uploadMedia,
  onUpdate,
  onEditingChange,
  showUnsavedError,
  isSelected = false,
  onToggleSelect,
  connectedProfiles = [],
  cardActions,
}) => {
  const safeProfiles = asConnectedProfileArray(connectedProfiles);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadMenu, setUploadMenu] = useState<{
    open: boolean;
    anchor: UploadMenuAnchor | "carouselAdd" | null;
  }>({ open: false, anchor: null });
  const toolbarUploadRef = useRef<HTMLDivElement | null>(null);
  const editUploadRef = useRef<HTMLDivElement | null>(null);
  const [editData, setEditData] = useState<DraftPost>({ ...post });
  // The post's own zone wins; otherwise default to the business profile's.
  const { timezone: profileTimezone } = useActiveScheduleTimezone();
  const editorTimezone = resolveScheduleTimezone(
    editData.scheduled_timezone || profileTimezone
  );
  
  const platformUpper = post.platform.toUpperCase();
  const basePlatform = platformUpper.includes("REELS") 
    ? platformUpper.split("_")[0] 
    : platformUpper;
    
  const isConnected = safeProfiles.some((p: ConnectedProfile) => {
    if (isGoogleBusinessProfilePlatform(post.platform)) {
      return isGoogleBusinessProfilePlatform(p.platform) && (p.details?.length ?? 0) > 0;
    }
    return p.platform.toUpperCase() === basePlatform && (p.details?.length ?? 0) > 0;
  });

  const variant = getPlatformVariant(post.platform);
  /** Latest post for async handlers (FileReader) — avoids stale `post` when merging carousel slides. */
  const postRef = useRef(post);
  postRef.current = post;
  const cardRef = useRef<HTMLDivElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const contentTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mediaUploadRef = useRef<HTMLDivElement | null>(null);
  const { mediaUploadRef: igMediaRef, carouselAddRef: igCarouselRef } =
    useInstagramCanvasDraft();
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;

  const showUploadMenu = uploadMenu.open;
  const uploadMenuAnchor = uploadMenu.anchor;

  const toggleUploadMenu = (anchor: UploadMenuAnchor | "carouselAdd") => {
    if (!uploadMedia) return;
    setUploadMenu((m) => {
      if (m.open && m.anchor === anchor) return { open: false, anchor: null };
      return { open: true, anchor };
    });
  };

  const closeUploadMenu = () => setUploadMenu({ open: false, anchor: null });

  const [localPickAnchor, setLocalPickAnchor] = useState<UploadMenuAnchor | "carouselAdd" | null>(null);
  const [cardValidationMessage, setCardValidationMessage] = useState<string | null>(null);


  const localFileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!uploadMedia) return;

    const files = Array.from(e.dataTransfer.files || []).filter(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    if (files.length === 0) return;

    setIsProcessing(true);

    if (post.instagram_content_format === "carousel") {
      const isFacebookCarousel = platformUpper === "FACEBOOK";
      const carouselFiles = isFacebookCarousel
        ? files.filter((f) => f.type.startsWith("image/"))
        : files;
      const newSlides: import("../types").InstagramCarouselSlide[] = [];
      const promises = carouselFiles.map((file) => {
        return new Promise<void>((resolve) => {
          if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (ev) => {
              newSlides.push({ url: ev.target?.result as string, type: "IMAGE" });
              resolve();
            };
            reader.readAsDataURL(file);
          } else {
            newSlides.push({ url: URL.createObjectURL(file), type: "VIDEO" });
            resolve();
          }
        });
      });

      await Promise.all(promises);

      if (isEditing) {
        setEditData((prev) => ({
          ...prev,
          carousel_slides: [...(prev.carousel_slides || []), ...newSlides].slice(0, 10),
        }));
      } else {
        const base = postRef.current;
        onUpdate?.({
          ...base,
          carousel_slides: [...(base.carousel_slides || []), ...newSlides].slice(0, 10),
        });
      }
    } else {
      const file = files[0];
      await new Promise<void>((resolve) => {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const url = ev.target?.result as string;
            if (isEditing) setEditData((prev) => ({ ...prev, image_url: url }));
            else onUpdate?.({ ...postRef.current, image_url: url });
            resolve();
          };
          reader.readAsDataURL(file);
        } else {
          const url = URL.createObjectURL(file);
          if (isEditing) setEditData((prev) => ({ ...prev, image_url: url }));
          else onUpdate?.({ ...postRef.current, image_url: url });
          resolve();
        }
      });
    }

    setIsProcessing(false);
  };

  // Only the Instagram carousel "add more" flow still reaches this input — the
  // single-image "Local files" button now goes through uploadMedia.onLocalFile,
  // which uploads to S3 instead of embedding a base64 data: URL (SGL: image
  // upload from computer via social chip).
  const handleLocalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // input.files is live: snapshot it into a real array before resetting
    // .value below, or the reset clears files too and the pick silently no-ops.
    const fileArray = Array.from(e.target.files || []).filter(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    if (e.target) e.target.value = ""; // reset input so same file can be picked again
    if (fileArray.length === 0) {
      setLocalPickAnchor(null);
      return;
    }

    setIsProcessing(true);

    const isFacebookCarousel = platformUpper === "FACEBOOK";
    const carouselFiles = isFacebookCarousel
      ? fileArray.filter((f) => f.type.startsWith("image/"))
      : fileArray;
    const newSlides: import("../types").InstagramCarouselSlide[] = [];
    const processPromises = carouselFiles.map((file) => {
      return new Promise<void>((resolve) => {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            newSlides.push({ url: ev.target?.result as string, type: "IMAGE" });
            resolve();
          };
          reader.readAsDataURL(file);
        } else {
          newSlides.push({ url: URL.createObjectURL(file), type: "VIDEO" });
          resolve();
        }
      });
    });

    await Promise.all(processPromises);

    if (isEditing) {
      setEditData((prev) => ({
        ...prev,
        carousel_slides: [...(prev.carousel_slides || []), ...newSlides].slice(0, 10),
        instagram_content_format: "carousel",
      }));
    } else {
      const base = postRef.current;
      onUpdate?.({
        ...base,
        carousel_slides: [...(base.carousel_slides || []), ...newSlides].slice(0, 10),
        instagram_content_format: "carousel",
      });
    }

    setIsProcessing(false);
    setLocalPickAnchor(null);
  };

  useEffect(() => {
    if (!showUploadMenu) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        toolbarUploadRef.current?.contains(t) ||
        mediaUploadRef.current?.contains(t) ||
        editUploadRef.current?.contains(t) ||
        igMediaRef.current?.contains(t) ||
        igCarouselRef.current?.contains(t)
      ) {
        return;
      }
      closeUploadMenu();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [showUploadMenu]);

  useEffect(() => {
    if (!isEditing) {
      setEditData({ ...post });
    } else {
      setEditData((prev) => {
        if (prev.image_url !== post.image_url) {
          return { ...prev, image_url: post.image_url };
        }
        return prev;
      });
    }
  }, [post, isEditing]);

  useEffect(() => {
    const isInstagram = variant === "instagram";
    if (!isInstagram) {
      if (cardValidationMessage) setCardValidationMessage(null);
      return;
    }
    const hasDirectImage = Boolean(post.image_url?.trim());
    const hasCarouselImage = Boolean(
      post.carousel_slides?.some((slide) => slide.type === "IMAGE" && slide.url?.trim())
    );
    if (hasDirectImage || hasCarouselImage) {
      setCardValidationMessage(null);
    }
  }, [post, variant, cardValidationMessage]);

  useEffect(() => {
    onEditingChangeRef.current?.(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusTarget =
      variant === "youtube" ? titleInputRef.current : contentTextareaRef.current;
    const id = window.setTimeout(() => focusTarget?.focus(), 120);
    return () => window.clearTimeout(id);
  }, [isEditing, variant]);

  const handleSave = () => {
    if (onUpdate) {
      onUpdate(editData);
    }
    setIsEditing(false);
  };

  
  const renderUploadMenuPopover = (placement: UploadMenuAnchor | "carouselAdd") => {
    if (!uploadMedia || !showUploadMenu || uploadMenuAnchor !== placement) return null;
    const { onLibrary, onLocalFile, onLibraryCarousel, onLibraryDocument } = uploadMedia;
    // LinkedIn-only: expose the "PDF Document" option in the upload menu.
    const showDocumentOption =
      variant === "linkedin" && Boolean(onLibraryDocument) && placement !== "carouselAdd";
    const openLibrary = () => {
      if (placement === "carouselAdd" && onLibraryCarousel) {
        onLibraryCarousel();
      } else {
        onLibrary();
      }
    };
    const placementClass =
      placement === "toolbar"
        ? "absolute top-full right-0 z-50 mt-2 min-w-[180px] rounded-lg border border-theme-border bg-theme-surface py-2 shadow-lg"
        : placement === "edit"
          ? "absolute bottom-full right-0 z-50 mb-2 min-w-[180px] rounded-lg border border-theme-border bg-theme-surface py-2 shadow-lg"
          : "absolute top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2 min-w-[180px] rounded-lg border border-theme-border bg-theme-surface py-2 shadow-xl";
    return (
      <div className={placementClass}>
        <button
          type="button"
          onClick={() => {
            openLibrary();
            closeUploadMenu();
          }}
          className="flex w-full items-center gap-2 px-4 py-2 text-left text-[14px] text-theme-fg transition-colors hover:bg-theme-secondary/15"
        >
          <span className="material-icons text-sm text-theme-fg-muted">photo_library</span>{" "}
          {placement === "carouselAdd" ? "My Library (multi-select)" : "My Library"}
        </button>
        <button
          type="button"
          onClick={() => {
            if (placement === "carouselAdd") {
              // Multi-file local pick has no real-upload equivalent yet; keep the local preview-only path.
              setLocalPickAnchor(placement);
              setTimeout(() => {
                localFileInputRef.current?.click();
                closeUploadMenu();
              }, 0);
              return;
            }
            onLocalFile();
            closeUploadMenu();
          }}
          className="flex w-full items-center gap-2 px-4 py-2 text-left text-[14px] text-theme-fg transition-colors hover:bg-theme-secondary/15"
        >
          <span className="material-icons text-sm text-theme-fg-muted">upload_file</span> Local files
        </button>
        {showDocumentOption && (
          <button
            type="button"
            onClick={() => {
              onLibraryDocument?.();
              closeUploadMenu();
            }}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-[14px] text-theme-fg transition-colors hover:bg-theme-secondary/15"
          >
            <span className="material-icons-outlined text-sm text-red-500">picture_as_pdf</span> PDF Document
          </button>
        )}
      </div>
    );
  };

  const handleRemovePhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    // LinkedIn document posts (SGL-174): if a PDF is attached, the same remove
    // button clears document_url. The card only shows one of image_url or
    // document_url at a time (document takes precedence in the render branch).
    const clearsDocument =
      variant === "linkedin" &&
      (Boolean(post.document_url) ||
        (Array.isArray(post.carousel_slides) &&
          post.carousel_slides.some((s) => s.type === "PDF")));
    if (isEditing) {
      if (clearsDocument) {
        handleChange("document_url", "");
        handleChange("carousel_slides", undefined);
      }
      handleChange("image_url", "");
      handleChange("video_url", "");
    } else {
      const next: DraftPost = { ...post, image_url: "", video_url: "" };
      if (clearsDocument) {
        next.document_url = "";
        next.carousel_slides = undefined;
      }
      onUpdate?.(next);
    }
  };

  const renderRemoveButton = (className: string = "top-2 right-2") => (
    <button
      type="button"
      onClick={handleRemovePhoto}
      className={`absolute ${className} z-20 flex h-7 w-7 items-center justify-center rounded-full bg-theme-danger text-white shadow-lg transition-all hover:scale-110 active:scale-95`}
      title="Remove media"
    >
      <span className="material-icons text-lg">close</span>
    </button>
  );

  const handleCancel = () => {
    setEditData({ ...post });
    setIsEditing(false);
  };

  const handleChange = (field: keyof DraftPost, value: any) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const renderEditForm = () => (
    <div className={`animate-scaleIn w-full rounded-[14px] border bg-theme-surface p-5 shadow-sm transition-all ${showUnsavedError ? "border-red-400" : "border-theme-border"}`}>
      {showUnsavedError && (
        <div className="flex items-center gap-3 rounded-lg bg-red-500/10 p-3 text-[12px] font-bold text-theme-danger border border-red-500/20 mb-4">
          <span className="material-icons text-base">warning</span>
          <span className="leading-tight">Please save your changes to this draft before<br/>saving all.</span>
        </div>
      )}
      
      <div className="flex items-center justify-between border-b border-theme-border pb-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-theme-surface shadow-sm ring-1 ring-black/5 overflow-hidden">
            {variant === "facebook" && PlatformLogos.facebook("w-6 h-6 text-theme-fg-muted")}
            {variant === "instagram" && PlatformLogos.instagram("w-6 h-6 text-theme-fg-muted")}
            {variant === "linkedin" && PlatformLogos.linkedin("w-6 h-6 text-theme-fg-muted")}
            {variant === "youtube" && PlatformLogos.youtube("w-6 h-6 text-theme-fg-muted")}
            {variant === "google_business" && <span className="material-icons text-2xl text-theme-fg-muted">campaign</span>}
            {variant === "default" && <span className="material-icons text-2xl text-theme-fg-muted">campaign</span>}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-bold text-theme-fg text-[14px] leading-none">
                {post.platform.charAt(0).toUpperCase() + post.platform.slice(1).toLowerCase()}
              </span>
              <ConnectionStatusBadge isConnected={isConnected} />
            </div>
            <div className="flex items-center gap-1 mt-1 text-[11px] text-theme-fg-muted font-medium">
              <span>{isConnected ? "Editing Draft" : "Not connected. Save as draft."}</span>
              <span className="material-icons text-[13px] opacity-60 leading-none">
                {isConnected ? "edit" : "save"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleCancel} className="px-3 py-1.5 text-[12px] font-bold text-theme-fg-muted hover:text-theme-fg transition-colors">Cancel</button>
          <button onClick={handleSave} className="rounded-lg bg-theme-primary px-4 py-1.5 text-[12px] font-bold text-white shadow-sm hover:opacity-90 transition-opacity">Save</button>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">Title</label>
          <input
            ref={variant === "youtube" ? titleInputRef : null}
            type="text"
            value={editData.title || ""}
            onChange={(e) => handleChange("title", e.target.value)}
            className="w-full rounded-[8px] border border-theme-border bg-theme-surface p-3 text-[13px] text-theme-fg outline-none transition-all focus:border-[#749B8F] focus:ring-1 focus:ring-[#749B8F]/30"
            placeholder={variant === "youtube" ? "Enter video title..." : "Post title..."}
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">Scheduled Time</label>
          <ScheduleDateTimeField
            value={editData.scheduled_time || ""}
            onChange={(value) => handleChange("scheduled_time", value)}
            timezone={editorTimezone}
            onTimezoneChange={(tz) => handleChange("scheduled_timezone", tz)}
            timezoneLabel="Timezone"
            className="rounded-[8px]"
          />
          {editData.scheduled_time && (
            <button
              type="button"
              onClick={() => handleChange("scheduled_time", "")}
              className="mt-1 text-[10px] font-bold text-theme-fg-muted hover:text-theme-fg"
            >
              Clear schedule
            </button>
          )}
        </div>

        <div className="relative space-y-1.5" ref={uploadMedia ? editUploadRef : undefined}>
          {uploadMedia && renderUploadMenuPopover("edit")}
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">Caption / Content</label>
          </div>
          {editData.image_url && (
            <div className="relative group w-20 h-20 mb-3">
              <div className="w-full h-full rounded-lg overflow-hidden border border-theme-border shadow-sm">
                <img src={editData.image_url} alt="Draft" className="w-full h-full object-cover" />
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleUploadMenu("edit")}
                  onKeyDown={(e) => e.key === "Enter" && toggleUploadMenu("edit")}
                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity"
                >
                  <span className="material-icons text-white text-lg">edit</span>
                </div>
              </div>
              {renderRemoveButton("-top-2 -right-2")}
            </div>
          )}
          {(editData.document_url ||
            (Array.isArray(editData.carousel_slides) &&
              editData.carousel_slides.some((s) => s.type === "PDF"))) && (
            <div className="relative group w-20 h-20 mb-3">
              <div className="w-full h-full rounded-lg overflow-hidden border border-theme-border shadow-sm bg-theme-background/30 flex flex-col items-center justify-center p-1 text-center">
                <span className="material-icons-outlined text-3xl text-red-500">
                  picture_as_pdf
                </span>
                <span className="text-[9px] font-semibold text-theme-fg line-clamp-1 leading-none mt-1">
                  {(() => {
                    if (editData.document_url) {
                      try {
                        const path = new URL(editData.document_url).pathname;
                        return decodeURIComponent(
                          path.split("/").pop() || "document.pdf"
                        );
                      } catch {
                        return "document.pdf";
                      }
                    }
                    const len = editData.carousel_slides?.length ?? 0;
                    return `${len} PDF${len > 1 ? "s" : ""}`;
                  })()}
                </span>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleUploadMenu("edit")}
                  onKeyDown={(e) => e.key === "Enter" && toggleUploadMenu("edit")}
                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity"
                >
                  <span className="material-icons text-white text-lg">edit</span>
                </div>
              </div>
              {renderRemoveButton("-top-2 -right-2")}
            </div>
          )}
          <textarea
            ref={contentTextareaRef}
            value={editData.content}
            onChange={(e) => handleChange("content", e.target.value)}
            className="min-h-[120px] w-full resize-none rounded-[8px] border border-theme-border bg-theme-surface p-3 text-[13px] text-theme-fg outline-none transition-all placeholder:text-gray-400 focus:border-[#749B8F] focus:ring-1 focus:ring-[#749B8F]/30 leading-relaxed"
            placeholder="Write your post content..."
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">Hashtags</label>
          <input
            type="text"
            value={editData.hashtags.join(", ")}
            onChange={(e) => handleChange("hashtags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
            className="w-full rounded-[8px] border border-theme-border bg-theme-surface p-3 text-[13px] text-theme-fg outline-none transition-all placeholder:text-gray-400 focus:border-[#749B8F] focus:ring-1 focus:ring-[#749B8F]/30"
            placeholder="tag1, tag2..."
          />
        </div>

        {editData.call_to_action && (
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">Call to Action</label>
            <input
              type="text"
              value={editData.call_to_action}
              onChange={(e) => handleChange("call_to_action", e.target.value)}
              className="w-full rounded-[8px] border border-theme-border bg-theme-surface p-3 text-[13px] text-theme-fg outline-none transition-all focus:border-[#749B8F] focus:ring-1 focus:ring-[#749B8F]/30"
            />
          </div>
        )}
      </div>
    </div>
  );

  /** Platform name row + Save / Schedule / Publish — directly under the platform label, above preview body. */
  const runValidatedCardAction = (action: "schedule" | "publish") => {
    if (!cardActions) return;
    const isInstagram = variant === "instagram";

    if (isInstagram && !instagramDraftHasPublishableMedia(post)) {
      setCardValidationMessage(INSTAGRAM_IMAGE_REQUIRED_MESSAGE);
      return;
    }

    setCardValidationMessage(null);
    if (action === "schedule") cardActions.onSchedule();
    else cardActions.onPublish();
  };

  /**
   * LinkedIn-only "Post to: Personal / Business Page" toggle. Personal and Business Page
   * both publish under platform "LINKEDIN"; this picks which connected profile (person vs
   * organization URN) the post targets. Only the connected option(s) are selectable.
   */
  const renderLinkedInTargetToggle = () => {
    if (variant !== "linkedin") return null;
    const { personal, business } = getConnectedLinkedInTargets(safeProfiles);
    if (!personal && !business) return null; // nothing connected — card already shows NOT CONNECTED

    const effective: "personal" | "business" =
      post.linkedinTarget ?? (business ? "business" : "personal");

    const choose = (t: "personal" | "business") => {
      if (!onUpdate || effective === t) return;
      onUpdate({ ...post, linkedinTarget: t });
    };

    const renderOption = (
      value: "personal" | "business",
      label: string,
      connected: boolean
    ) => (
      <button
        type="button"
        disabled={!connected || !onUpdate}
        onClick={(e) => {
          e.stopPropagation();
          choose(value);
        }}
        title={connected ? undefined : "Connect this LinkedIn account in Connections to use it."}
        className={`flex-1 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          effective === value
            ? "bg-theme-primary text-white shadow-sm"
            : "bg-theme-surface text-theme-fg-muted hover:bg-theme-background"
        }`}
      >
        {label}
      </button>
    );

    return (
      <div className="border-b border-theme-border bg-theme-background/40 px-4 py-2">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">
          Post to
        </div>
        <div className="flex gap-2 rounded-xl bg-theme-border/20 p-1">
          {renderOption("personal", "Personal", personal)}
          {renderOption("business", "Business Page", business)}
        </div>
      </div>
    );
  };

  const renderPostHeaderWithActions = () => (
    <>
      <PostCardHeader
        platform={post.platform}
        scheduledTime={post.scheduled_time}
        scheduledTimezone={post.scheduled_timezone}
        isConnected={isConnected}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
        onEdit={() => setIsEditing(true)}
        variant={variant}
      />
      {renderLinkedInTargetToggle()}
      {cardActions ? (
        <CardActionButtons
          actions={{
            ...cardActions,
            onSchedule: () => runValidatedCardAction("schedule"),
            onPublish: () => runValidatedCardAction("publish"),
          }}
          validationMessage={cardValidationMessage}
        />
      ) : null}
    </>
  );

  const renderCardContainer = (content: React.ReactNode) => (
    <div
      className={`w-full overflow-hidden rounded-[10px] border shadow-sm transition-all duration-300 hover:shadow-md dark:bg-theme-surface ${isSelected ? "border-theme-primary/40 bg-theme-primary/[0.02] ring-2 ring-theme-primary/15" : "border-theme-border/40"} bg-theme-surface ${dragActive ? "border-theme-primary ring-2 ring-theme-primary/20 bg-theme-primary/5" : ""}`}
    >
      {content}
      {post.platform_post_id && (
        <div className="border-t border-theme-border bg-theme-background/30 px-4 py-2">
          <LivePostLink
            platform={post.platform}
            platformPostId={post.platform_post_id}
            business_profile_id={post.business_profile_id}
            platform_response={post.platform_response}
            isDocumentPost={
              Boolean(post.document_url) ||
              Boolean(post.carousel_slides?.some((s) => s.type === "PDF"))
            }
          />
        </div>
      )}
    </div>
  );

  const renderCardContent = () => {
    switch(variant) {
      case "linkedin":
        return renderCardContainer(
          <>
            {renderPostHeaderWithActions()}
            <div className="px-4 pt-4 pb-3">
              {post.title && (
                <h4 className="mb-2 text-[15px] font-bold leading-tight tracking-tight text-theme-fg">{post.title}</h4>
              )}
              <div className="text-[14px] text-theme-fg whitespace-pre-wrap leading-relaxed tracking-tight">{post.content}</div>
              {post.hashtags && post.hashtags.length > 0 && (
                <div className="mt-2 text-[14px] text-theme-fg-muted font-semibold flex flex-wrap gap-x-1.5">
                  {post.hashtags.map(tag => (tag.startsWith("#") ? tag : `#${tag}`)).join(" ")}
                </div>
              )}
            </div>
            <div
              ref={uploadMedia ? mediaUploadRef : undefined}
              className="relative w-full"
            >
              {uploadMedia && renderUploadMenuPopover("media")}
              {Array.isArray(post.carousel_slides) &&
              post.carousel_slides.some((s) => s.type === "PDF") ? (
                <div className="relative border-y border-theme-border/30 bg-theme-background/10 px-6 py-8">
                  <div className="flex flex-col items-center justify-center text-center">
                    <span className="material-icons-outlined mb-2 text-5xl text-red-500">
                      picture_as_pdf
                    </span>
                    <span className="line-clamp-2 break-all text-xs font-semibold text-theme-fg">
                      {post.carousel_slides.length} PDF Document
                      {post.carousel_slides.length > 1 ? "s" : ""}
                    </span>
                    <div className="mt-2 max-w-xs flex flex-col gap-1">
                      {post.carousel_slides.map((slide, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 justify-center">
                          <span className="text-[10px] text-theme-fg-muted font-medium">
                            File {idx + 1}:
                          </span>
                          <a
                            href={slide.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] font-medium text-theme-primary underline truncate max-w-[180px]"
                          >
                            {(() => {
                              try {
                                const path = new URL(slide.url).pathname;
                                return decodeURIComponent(
                                  path.split("/").pop() || `document-${idx + 1}.pdf`
                                );
                              } catch {
                                return `document-${idx + 1}.pdf`;
                              }
                            })()}
                          </a>
                        </div>
                      ))}
                    </div>
                    <span className="mt-3 rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
                      Merged PDF Document
                    </span>
                  </div>
                  {renderRemoveButton()}
                </div>
              ) : post.document_url ? (
                <div className="relative border-y border-theme-border/30 bg-surface-ui px-6 py-8">
                  <div className="flex flex-col items-center justify-center text-center">
                    <span className="material-icons-outlined mb-2 text-5xl text-red-500">
                      picture_as_pdf
                    </span>
                    <span className="line-clamp-2 break-all text-xs font-semibold text-theme-fg">
                      {(() => {
                        try {
                          const path = new URL(post.document_url).pathname;
                          return decodeURIComponent(path.split("/").pop() || "document.pdf");
                        } catch {
                          return "document.pdf";
                        }
                      })()}
                    </span>
                    <span className="mt-2 rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
                      PDF Document
                    </span>
                    <a
                      href={post.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 text-[11px] font-medium text-theme-primary underline"
                    >
                      Open in new tab
                    </a>
                  </div>
                  {renderRemoveButton()}
                </div>
              ) : post.video_url ? (
                <div className="relative border-y border-theme-border/30">
                  <video
                    key={post.video_url}
                    src={post.video_url}
                    controls
                    playsInline
                    className="w-full h-auto max-h-[500px] object-contain mx-auto"
                  />
                  {renderRemoveButton()}
                </div>
              ) : post.image_url ? (
                <div className="relative border-y border-theme-border/30">
                  <img src={post.image_url} alt="Post" className="w-full h-auto max-h-[500px] object-contain mx-auto" />
                  {renderRemoveButton()}
                </div>
              ) : (
                <EmptyStatePlaceholder onClick={() => toggleUploadMenu("media")} />
              )}
            </div>
          </>
        );
      case "instagram":
        return renderCardContainer(
          <>
            {renderPostHeaderWithActions()}
            <InstagramCanvasPreview
              post={post}
              onUpdate={(next) => onUpdate?.(next)}
              uploadMedia={uploadMedia}
              mediaUploadRef={igMediaRef}
              carouselAddRef={igCarouselRef}
              renderUploadMenuPopover={renderUploadMenuPopover}
              toggleUploadMenu={toggleUploadMenu}
              onRemoveHeroMedia={() => {
                const fmt = getInstagramFormat(post);
                if (fmt === "video") {
                  onUpdate?.({ ...post, video_url: "", image_url: "" });
                } else {
                  onUpdate?.({
                    ...post,
                    image_url: "",
                    carousel_slides: undefined,
                    instagram_content_format: "image",
                  });
                }
              }}
            />
          </>
        );
      case "facebook":
        return renderCardContainer(
          <>
            {renderPostHeaderWithActions()}
            <InstagramCanvasPreview
              post={post}
              editorPlatform="FACEBOOK"
              onUpdate={(next) => onUpdate?.(next)}
              uploadMedia={uploadMedia}
              mediaUploadRef={igMediaRef}
              carouselAddRef={igCarouselRef}
              renderUploadMenuPopover={renderUploadMenuPopover}
              toggleUploadMenu={toggleUploadMenu}
              onRemoveHeroMedia={() => {
                const fmt = getInstagramFormat(post);
                if (fmt === "video") {
                  onUpdate?.({ ...post, video_url: "", image_url: "" });
                } else {
                  onUpdate?.({
                    ...post,
                    image_url: "",
                    carousel_slides: undefined,
                    content_format: "image",
                    instagram_content_format: "image",
                  });
                }
              }}
            />
          </>
        );
      case "google_business":
        return renderCardContainer(
          <>
            {renderPostHeaderWithActions()}
            <div className="px-4 py-4">
              {post.title && (
                <h4 className="mb-2 text-[15px] font-bold leading-tight tracking-tight text-theme-fg">{post.title}</h4>
              )}
              <div className="text-[14px] text-theme-fg whitespace-pre-wrap leading-relaxed">{post.content}</div>
              {post.hashtags && post.hashtags.length > 0 && (
                <div className="mt-2 text-[14px] text-theme-fg-muted font-medium flex flex-wrap gap-x-1.5">
                  {post.hashtags.map(tag => (tag.startsWith("#") ? tag : `#${tag}`)).join(" ")}
                </div>
              )}
            </div>
            <div
              ref={uploadMedia ? mediaUploadRef : undefined}
              className="relative w-full"
            >
              {uploadMedia && renderUploadMenuPopover("media")}
              {post.image_url ? (
                <div className="relative border-y border-theme-border/30">
                  <img src={post.image_url} alt="Post" className="h-full w-full object-cover" />
                  {renderRemoveButton()}
                </div>
              ) : (
                <EmptyStatePlaceholder onClick={() => toggleUploadMenu("media")} />
              )}
            </div>
            {editData.call_to_action && (
              <div className="px-4 py-3 border-t border-theme-border/50 bg-theme-background/30">
                <div className="inline-flex items-center justify-center rounded-full border border-theme-fg-muted px-5 py-1.5 text-[13px] font-bold text-theme-fg-muted">
                  {editData.call_to_action}
                </div>
              </div>
            )}
          </>
        );
      case "youtube":
      default:
        return renderCardContainer(
          <>
            {renderPostHeaderWithActions()}
            <div className="p-4 flex flex-col gap-3">
              {post.title && (
                <h4 className="text-[15px] font-bold leading-tight tracking-tight text-theme-fg">{post.title}</h4>
              )}
              <div className="min-w-0">
                <div className="text-sm text-theme-fg whitespace-pre-wrap leading-relaxed">{post.content}</div>
              </div>
            </div>
            <div
              ref={uploadMedia ? mediaUploadRef : undefined}
              className="relative w-full"
            >
              {uploadMedia && renderUploadMenuPopover("media")}
              {post.image_url ? (
                <div className="relative border-y border-theme-border/30 bg-black/5">
                  <div className="aspect-video w-full">
                    <img src={post.image_url} alt="YouTube Thumbnail" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                      <span className="material-icons text-white text-5xl opacity-80">play_circle_filled</span>
                    </div>
                  </div>
                  {renderRemoveButton()}
                </div>
              ) : (
                <EmptyStatePlaceholder onClick={() => toggleUploadMenu("media")} />
              )}
            </div>
          </>
        );
    }
  };
  return (
    <div
      ref={cardRef}
      className="relative group/card w-full max-w-[540px] mx-auto flex flex-col transition-all duration-300"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Processing Overlay */}
      {isProcessing && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[2px] rounded-[10px]">
          <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-theme-primary border-t-transparent shadow-sm mb-3" />
          <span className="text-sm font-bold text-theme-primary/90 tracking-wide">Processing media...</span>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`relative z-[1] w-full transition-all duration-300 rounded-[10px] ${dragActive && isEditing ? "ring-2 ring-theme-primary/20 bg-theme-primary/5" : ""}`}>
        {isEditing ? (
          <div className="mx-auto w-full max-w-[540px]">
            {renderEditForm()}
          </div>
        ) : renderCardContent()}
      </div>

      <input 
        type="file" 
        accept={platformUpper === "FACEBOOK" && localPickAnchor === "carouselAdd" ? "image/*" : "image/*,video/*"}
        className="hidden" 
        ref={localFileInputRef}
        onChange={handleLocalFileChange} 
        multiple={localPickAnchor === "carouselAdd"}
      />
    </div>
  );
};
